import {
  Business,
  Close,
  SearchOutlined,
  SupervisorAccountOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { OrganizationAPI } from "../../../../api";
import AddressAPI from "../../../../api/address";
import { Account, Organization } from "../../../../model";
import { Address, OrganizationAddressLabels } from "../../../../model/Address";
import { useAccountExecutionContext } from "../../../task/components/task-types/account/CreateAccountExecutionComponent";
import {
  AccountSelectTaskContext,
  AccountSelectTaskOption,
} from "../AccountSelectWrapper";

const AccountSelectExpanded = ({
  setSelected,
}: {
  setSelected: (option?: AccountSelectTaskOption) => void;
}) => {
  const { selected } = useContext(AccountSelectTaskContext);
  const createAccountContext = useAccountExecutionContext();
  const { options, disabled, query, setQuery } = useContext(
    AccountSelectTaskContext,
  );

  useEffect(() => {
    createAccountContext?.setActionBarVisible(options.length > 0);
  }, [selected, options.length, createAccountContext]);

  return (
    <Container disableGutters>
      {(selected && <OptionSelectedCard setSelected={setSelected} />) || (
        <TextField
          InputProps={{
            startAdornment: <SearchOutlined sx={{ mr: 1 }} />,
            endAdornment: query && (
              <Tooltip title="Clear Search">
                <IconButton onClick={() => setQuery("")}>
                  <Close />
                </IconButton>
              </Tooltip>
            ),
          }}
          placeholder="Search Directory"
          fullWidth
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mb: "1rem" }}
          value={query ?? ""}
          disabled={disabled}
        />
      )}

      {selected?.type === "organization" &&
        !selected.email &&
        !(selected.data as Organization).email && (
          <Alert severity="warning" sx={{ mb: "1rem" }}>
            There is no email associated with this account, and they will not be
            notified.
          </Alert>
        )}

      {(options.length > 0 && <OptionsList onSelect={setSelected} />) || <></>}
    </Container>
  );
};

// Displays the selected option as a card or a button to create a new account
const OptionSelectedCard = ({
  setSelected,
}: {
  setSelected: (option?: AccountSelectTaskOption) => void;
}) => {
  const { selected, disabled } = useContext(AccountSelectTaskContext);

  const [address, setAddress] = useState<Address | undefined>(undefined);
  const [addressLoaded, setAddressLoaded] = useState(false);

  const [cardLoaded, setCardLoaded] = useState(false);

  const [linkedOrganization, setLinkedOrganization] = useState<
    Organization | undefined
  >(undefined);

  useEffect(() => {
    if (!(selected?.data as Account).external_org_id) {
      setLinkedOrganization(undefined);
      setCardLoaded(true);
      return;
    }

    setCardLoaded(false);

    const organizationId = (selected?.data as Account).external_org_id;
    if (!organizationId) return;

    OrganizationAPI.get(organizationId).then((organization) => {
      if (organization) {
        setLinkedOrganization(organization);
      } else {
        setLinkedOrganization(undefined);
      }
      setCardLoaded(true);
    });
  }, [selected]);

  useEffect(() => {
    if (selected?.type !== "organization" && !linkedOrganization) {
      setAddress(undefined);
      setAddressLoaded(true);
      return;
    }

    setAddressLoaded(false);

    let useId = selected?.data.id;
    if (!useId || selected?.type == "account") {
      useId = linkedOrganization?.id;
    }

    if (!useId) return;

    AddressAPI.getOrganizationAddressByLabel(
      useId,
      OrganizationAddressLabels.PRIMARY,
    ).then((address) => {
      if (address) {
        setAddress(address);
      } else {
        setAddress(undefined);
      }
      setAddressLoaded(true);
      setCardLoaded(true);
    });
  }, [selected, linkedOrganization]);

  const getOrganizationContent = (linkedOrganization?: Organization) => {
    const organization = linkedOrganization || (selected!.data as Organization);

    return (
      <Box display={"flex"} alignItems={"center"} width={"100%"}>
        {organization.theme?.shortLogoUrl &&
          organization.theme?.shortLogoUrl != "" && (
            <img
              src={organization.theme?.shortLogoUrl}
              style={{ width: "5rem", marginRight: "1rem" }}
            />
          )}
        <Box flex={2}>
          <Typography
            variant="inherit"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selected!.label}
          </Typography>
        </Box>
        <Box>
          {addressLoaded && address && (
            <Box display={"flex"} flexDirection={"column"}>
              <Typography
                variant="caption"
                sx={{ color: (theme) => theme.palette.grey[600] }}
              >
                {address?.first_line}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: (theme) => theme.palette.grey[600] }}
              >
                {address?.second_line}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: (theme) => theme.palette.grey[600] }}
              >
                {address?.city}, {address?.state} {address?.zip_code}
              </Typography>
            </Box>
          )}
          {!addressLoaded && (
            <Skeleton variant="rounded" width={150} height={60} />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignContent: "center",
            ml: 2,
          }}
        >
          <IconButton
            onClick={() => setSelected(undefined)}
            size="small"
            disabled={disabled}
            sx={{ aspectRatio: "1/1" }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const getAccountContent = () => {
    const account = selected!.data as Account;
    const hasEmail = account.primary_email && account.primary_email.length > 0;

    return (
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <Box>
          <Typography variant="inherit">{selected!.label}</Typography>
          <Typography
            variant="subtitle2"
            sx={{
              color: (theme) => theme.palette.grey[600],
            }}
          >
            {hasEmail ? (
              <Link
                to={`mailto:${account.primary_email}`}
                style={{ color: "inherit" }}
              >
                {account.primary_email}
              </Link>
            ) : (
              "No email provided"
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignContent: "center",
          }}
        >
          <IconButton
            onClick={() => setSelected(undefined)}
            size="small"
            disabled={disabled}
            sx={{ aspectRatio: "1/1" }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const content = selected && (
    <Paper
      elevation={0}
      sx={{
        height: "6rem",
        p: 2,
        backgroundColor: "rgba(0, 0, 0, 0.03)",
        borderRadius: "7px",
        boxShadow: "0 2px 1px rgba(0,0,0,0.1)",
      }}
    >
      {cardLoaded ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          {selected.type === "organization" ||
          (selected.data as Account).external_org_id
            ? getOrganizationContent(linkedOrganization)
            : getAccountContent()}
        </Box>
      ) : (
        <Skeleton variant="rounded" width={"100%"} height={"100%"} />
      )}
    </Paper>
  );

  return (
    <Container disableGutters sx={{ mb: 2, height: "6rem" }}>
      {content}
    </Container>
  );
};

const OptionsList = ({
  onSelect,
}: {
  onSelect: (option?: AccountSelectTaskOption) => void;
}) => {
  const { selected, options, disabled } = useContext(AccountSelectTaskContext);

  const getOptionIcon = (option: AccountSelectTaskOption) => {
    if (option.type === "organization") {
      return (
        <Tooltip title="Organization">
          <Business />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="Account">
          <SupervisorAccountOutlined />
        </Tooltip>
      );
    }
  };
  const content = useMemo(() => {
    return options.map((option) => {
      if (option.data.id != selected?.data.id) {
        return (
          <ListItem disableGutters disablePadding key={option.data.id}>
            <ListItemButton
              onClick={() => onSelect(option)}
              disabled={disabled}
              key={option.data.id}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.03)",
                  borderRadius: "8px",
                },
              }}
            >
              <ListItemIcon>{getOptionIcon(option)}</ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </ListItemButton>
          </ListItem>
        );
      } else {
        return (
          <ListItem
            sx={{
              bgcolor: "rgba(0, 0, 0, 0.03)",
            }}
            secondaryAction={
              <IconButton
                onClick={() => onSelect(undefined)}
                size="small"
                disabled={disabled}
              >
                <Close fontSize="small" />
              </IconButton>
            }
            key={selected?.data.id}
          >
            <ListItemIcon>{getOptionIcon(selected!)}</ListItemIcon>
            <ListItemText>{selected?.label}</ListItemText>
          </ListItem>
        );
      }
    });
  }, [disabled, onSelect, options, selected]);

  return (
    <List
      sx={{
        bgcolor: "background.paper",
        borderRadius: "8px",
        py: 1,
        px: 1,
        maxHeight: "40vh",
        overflowY: "auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        "& .MuiListItem-root": {
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,0.1)",
          borderRadius: "8px",
        },
      }}
    >
      {content}
    </List>
  );
};

export default AccountSelectExpanded;
